import { FormInstance, useForm, useWatch } from 'antd/lib/form/Form';
import Modal from 'antd/lib/modal/Modal';
import { flatMap } from 'lodash';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { UpdateIntegrationRequestDataType } from '../actions/integrations';
import {
    selectAppSettings,
    selectAppStoreType,
    selectOrgProviders,
    selectAppName,
    selectWorkflowEnabledSteps,
} from '../selectors/selectors';
import { FormFieldData, integrationModalContentUsingFormInstance } from '../helpers/formHelpers';
import { updateIntegrationFormDataForIntegration } from '../helpers/integrationFormHelpers';
import { validateUpdateIntegrationValues } from '../helpers/integrationHelpers';
import { RootState } from '../reducers/reducers';
import {
    AppPlatform,
    AppStoreType,
    Integration,
    IntegrationAppCenterBeta,
    IntegrationAzureIT,
    IntegrationFirebaseBeta,
    IntegrationInstallationId,
    IntegrationProvider,
    IntegrationTestFlight,
    IntegrationType,
    WorkflowEnabledSteps,
} from '../types';
import { AppComponentProps, AppRouteComponentMatchParams } from '../typescriptHelpers';
import { additionalInfoNodeForProvider } from './IntegrationConfiguration';
import { globalMessage } from '../utils/globalMessage';
import { useEffect } from 'react';

interface EditIntegrationModalModalProps extends AppComponentProps {
    integration: Integration;
    open: boolean;
    isLoading: boolean;
    updateIntegration: (
        integrationInstallationId: IntegrationInstallationId,
        integrationType: IntegrationType,
        data: UpdateIntegrationRequestDataType,
    ) => void;
    onCancel: (integrationId: IntegrationInstallationId) => void;
}

const EditIntegrationModal: React.FC<EditIntegrationModalModalProps> = (props: EditIntegrationModalModalProps) => {
    const { orgIdParam, appIdParam } = useParams<AppRouteComponentMatchParams>();
    const [formIsValid, setFormIsValid] = React.useState(false);
    const [form] = useForm();

    const [selectedTestflightBundleId, setSelectedTestflightBundleId] = React.useState(
        (props.integration as IntegrationTestFlight)?.data.bundleId ?? null,
    );
    const [selectedAppCenterAppId, setSelectedAppCenterAppId] = React.useState(
        (props.integration as IntegrationAppCenterBeta)?.data?.selectedApp?.id ?? null,
    );
    const [selectedAzureProjectId, setSelectedAzureProjectId] = React.useState(
        (props.integration as IntegrationAzureIT)?.data.selectedProject?.id ?? null,
    );
    const [selectedFirebaseProjectId, setSelectedFirebaseProjectId] = React.useState(
        (props.integration as IntegrationFirebaseBeta)?.data.projectId ?? null,
    );
    const integrationProvider = useSelector((state: RootState) => {
        const integrationProviders = selectOrgProviders(state, props.orgId);
        const provider = integrationProviders.find((provider) => provider.id === props.integration.providerId);

        return provider ?? null;
    });

    const appPlatform = useSelector((state: RootState) => selectAppSettings(state, props.appId).platform);
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, props.appId));
    const appName = useSelector((state: RootState) => selectAppName(state, props.appId));
    const workflowEnabledSteps = useSelector((state: RootState) => selectWorkflowEnabledSteps(state, props.appId));

    const onFinish = (
        integrationInstallationId: IntegrationInstallationId,
        integrationId: IntegrationType,
        form: FormInstance,
    ) => {
        form.validateFields()
            .then((values) => {
                const requestData = validateUpdateIntegrationValues(integrationId, integrationProvider, values);

                if (requestData) {
                    props.updateIntegration(integrationInstallationId, integrationId, requestData);
                } else {
                    throw Error('Invalid form values');
                }
            })
            .catch((error) => {
                globalMessage.error(error.message);
                setFormIsValid(false);
            });
    };

    const onFieldsChange = (_: IntegrationType, form: FormInstance) => {
        const errors = flatMap(form.getFieldsError().map((field) => field.errors));

        const isValid = errors.length === 0;

        setFormIsValid(isValid);
    };

    const testFlightBundleIdChanged = (value: string) => {
        form.resetFields(['selectedBetaGroupIds']);
        setSelectedTestflightBundleId(value);
    };

    const appCenterAppIdChanged = (value: string) => {
        setSelectedAppCenterAppId(value);
        form.resetFields(['selectedBetaGroupIds']);
    };

    const firebaseProjectIdChanged = (value: string) => {
        setSelectedFirebaseProjectId(value);
        form.resetFields(['firebaseAppId', 'selectedBetaGroupIds']);
    };

    const azureProjectIdChanged = (value: string) => {
        form.resetFields(['projects']);
        setSelectedAzureProjectId(value);
    };

    const selectedAutoSelectAllJiraProjects = useWatch(['autoSelectAllProjects'], form);
    useEffect(() => {
        form.resetFields(['projects']);
    }, [selectedAutoSelectAllJiraProjects]);

    const selectedBitriseIsPipeline = useWatch(['bitriseIsPipeline'], form);
    const selectedBitriseIsPipelineDev = useWatch(['bitriseIsPipelineDev'], form);
    const selectedBitriseIsPipelineProd = useWatch(['bitriseIsPipelineProd'], form);

    const fields = editDataForIntegration({
        integration: props.integration,
        appPlatform,
        appStoreType,
        appId: props.appId,
        appName,
        workflowEnabledSteps,
        onChangeTestFlightBundleId: testFlightBundleIdChanged,
        onChangeAppCenterBetaAppId: appCenterAppIdChanged,
        onChangeAzureProjectId: azureProjectIdChanged,
        onChangeFirebaseProjectId: firebaseProjectIdChanged,
        integrationProvider,
        testflightBundleId: selectedTestflightBundleId,
        appCenterAppId: selectedAppCenterAppId,
        azureProjectId: selectedAzureProjectId,
        firebaseProjectId: selectedFirebaseProjectId,
        autoSelectAllJiraProjects: selectedAutoSelectAllJiraProjects,
        bitriseIsPipeline: selectedBitriseIsPipeline,
        bitriseIsPipelineDev: selectedBitriseIsPipelineDev,
        bitriseIsPipelineProd: selectedBitriseIsPipelineProd,
    });

    const modalContent = integrationModalContentUsingFormInstance(
        form,
        'vertical',
        props.integration.id,
        props.integration.installationId,
        fields,
        onFieldsChange,
        onFinish,
    );
    const additionalInfoForProvider =
        integrationProvider ?
            additionalInfoNodeForProvider(
                integrationProvider.type,
                props.integration.id,
                props.orgId,
                orgIdParam,
                appIdParam,
                props.integration.installationId,
            )
        :   null;

    return (
        <Modal
            title={`${props.integration.name} settings`}
            open={props.open}
            onCancel={() => props.onCancel(props.integration.installationId)}
            onOk={() => form.submit()}
            confirmLoading={props.isLoading}
            okButtonProps={{ disabled: !formIsValid }}
            okText="Update"
            width={props.integration.id === IntegrationType.CircleCI ? 700 : 520}
            destroyOnClose
            afterClose={() => {
                form.resetFields();
                setFormIsValid(false);
            }}
        >
            <div>
                {modalContent}
                {additionalInfoForProvider && <div style={{ marginTop: 12 }}>{additionalInfoForProvider}</div>}
            </div>
        </Modal>
    );
};

export default EditIntegrationModal;

export const editDataForIntegration: (configData: {
    integration: Integration;
    appPlatform: AppPlatform;
    appStoreType: AppStoreType;
    appId: string;
    appName: string;
    workflowEnabledSteps: WorkflowEnabledSteps;
    onChangeTestFlightBundleId: (value) => void;
    onChangeAppCenterBetaAppId: (value) => void;
    onChangeAzureProjectId: (value) => void;
    onChangeFirebaseProjectId: (value) => void;
    integrationProvider?: IntegrationProvider;
    testflightBundleId?: string;
    azureProjectId?: string;
    appCenterAppId?: string;
    firebaseProjectId?: string;
    autoSelectAllJiraProjects?: boolean;
    bitriseIsPipeline?: boolean;
    bitriseIsPipelineDev?: boolean;
    bitriseIsPipelineProd?: boolean;
}) => FormFieldData[] = (configData) => {
    const {
        integration,
        appPlatform,
        appStoreType,
        appId,
        appName,
        workflowEnabledSteps,
        onChangeTestFlightBundleId,
        onChangeAppCenterBetaAppId,
        onChangeAzureProjectId,
        onChangeFirebaseProjectId,
        integrationProvider,
        testflightBundleId,
        azureProjectId,
        appCenterAppId,
        firebaseProjectId,
        autoSelectAllJiraProjects,
        bitriseIsPipeline,
        bitriseIsPipelineDev,
        bitriseIsPipelineProd,
    } = configData;

    return updateIntegrationFormDataForIntegration(
        integration,
        appPlatform,
        appStoreType,
        appId,
        appName,
        workflowEnabledSteps,
        integrationProvider,
        onChangeTestFlightBundleId,
        testflightBundleId,
        onChangeAppCenterBetaAppId,
        appCenterAppId,
        onChangeAzureProjectId,
        azureProjectId,
        null,
        null,
        null,
        null,
        onChangeFirebaseProjectId,
        firebaseProjectId,
        null,
        null,
        null,
        null,
        autoSelectAllJiraProjects,
        bitriseIsPipeline,
        bitriseIsPipelineDev,
        bitriseIsPipelineProd,
    );
};
